import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus,
  Trash2,
  Play,
  Copy,
  Check,
  ExternalLink,
  Shield,
  Key,
  Globe,
  Link2,
  Settings,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Eye,
  EyeOff,
  BookOpen,
  Boxes,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider icons and colors
const providerStyles = {
  bubble: {
    name: 'Bubble.io',
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-500/10',
    logo: 'https://s3.amazonaws.com/appforest_uf/f1542037825580x604474375034491800/bubble-logo-vector.svg'
  },
  google: {
    name: 'Google',
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgLight: 'bg-red-500/10',
    logo: 'https://www.google.com/favicon.ico'
  },
  microsoft: {
    name: 'Microsoft Azure AD',
    color: 'bg-blue-600',
    textColor: 'text-blue-600',
    bgLight: 'bg-blue-600/10',
    logo: 'https://www.microsoft.com/favicon.ico'
  },
  custom: {
    name: 'Custom OAuth',
    color: 'bg-gray-500',
    textColor: 'text-gray-500',
    bgLight: 'bg-gray-500/10',
    logo: null
  }
};

export default function AdminOAuthSSO() {
  const [providers, setProviders] = useState([]);
  const [availableProviders, setAvailableProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [bubbleInstructions, setBubbleInstructions] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [testResults, setTestResults] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    provider_type: 'bubble',
    name: '',
    client_id: '',
    client_secret: '',
    config: {
      bubble_app_name: '',
      scopes: 'openid profile email',
      auto_create_users: true
    }
  });

  useEffect(() => {
    fetchProviders();
    fetchAvailableProviders();
    fetchBubbleInstructions();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/oauth/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/oauth/providers/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableProviders(data);
      }
    } catch (error) {
      console.error('Error fetching available providers:', error);
    }
  };

  const fetchBubbleInstructions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/oauth/instructions/bubble`);
      if (response.ok) {
        const data = await response.json();
        setBubbleInstructions(data);
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleCreateProvider = async () => {
    if (!formData.name || !formData.client_id || !formData.client_secret) {
      toast.error('Nombre, Client ID y Client Secret son requeridos');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/oauth/providers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Proveedor OAuth creado exitosamente');
        setShowDialog(false);
        resetForm();
        fetchProviders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear proveedor');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleDeleteProvider = async (id) => {
    if (!confirm('¿Eliminar este proveedor OAuth?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/oauth/providers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Proveedor eliminado');
        fetchProviders();
      }
    } catch (error) {
      toast.error('Error al eliminar proveedor');
    }
  };

  const handleTestProvider = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/oauth/providers/${id}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      setTestResults(prev => ({ ...prev, [id]: result }));
      
      if (result.success) {
        toast.success('Todas las pruebas pasaron');
      } else {
        toast.warning('Algunas pruebas fallaron');
      }
    } catch (error) {
      toast.error('Error al probar proveedor');
    }
  };

  const resetForm = () => {
    setFormData({
      provider_type: 'bubble',
      name: '',
      client_id: '',
      client_secret: '',
      config: {
        bubble_app_name: '',
        scopes: 'openid profile email',
        auto_create_users: true
      }
    });
  };

  const toggleShowSecret = (id) => {
    setShowSecrets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen" data-testid="admin-oauth-page">
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">OAuth SSO</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowInstructions(true)}>
            <BookOpen className="h-4 w-4 mr-1" /> Guía Bubble.io
          </Button>
          <Button size="sm" onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nuevo Proveedor
          </Button>
        </div>
      </header>

      <div className="p-6 space-y-6 fade-in">
        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-purple-500/20">
                <Key className="h-6 w-6 text-purple-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Single Sign-On (SSO)</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Configura proveedores OAuth 2.0 para permitir que los usuarios de tu plataforma inicien sesión 
                  en OkReserva usando sus cuentas existentes. Soportamos Bubble.io, Google, Microsoft y proveedores personalizados.
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(providerStyles).map(([key, style]) => (
                    <Badge key={key} variant="outline" className={`${style.bgLight} ${style.textColor} border-0`}>
                      {style.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="providers" className="space-y-4">
          <TabsList>
            <TabsTrigger value="providers">Proveedores Configurados</TabsTrigger>
            <TabsTrigger value="bubble">Guía Bubble.io</TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p>Cargando proveedores...</p>
              </div>
            ) : providers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-semibold mb-2">No hay proveedores configurados</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Configura tu primer proveedor OAuth para habilitar Single Sign-On
                  </p>
                  <Button onClick={() => setShowDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Agregar Proveedor
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {providers.map((provider) => {
                  const style = providerStyles[provider.provider_type] || providerStyles.custom;
                  const testResult = testResults[provider.id];
                  
                  return (
                    <Card key={provider.id} className="overflow-hidden">
                      <div className={`h-1 ${style.color}`} />
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${style.bgLight}`}>
                              {style.logo ? (
                                <img src={style.logo} alt={style.name} className="h-6 w-6" onError={(e) => e.target.style.display='none'} />
                              ) : (
                                <Globe className={`h-6 w-6 ${style.textColor}`} />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{provider.name}</h3>
                                <Badge variant={provider.is_active ? "default" : "secondary"}>
                                  {provider.is_active ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">
                                {style.name} • Creado: {new Date(provider.created_at).toLocaleDateString()}
                              </p>
                              
                              {/* Credentials */}
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-24">Client ID:</span>
                                  <code className="bg-muted px-2 py-1 rounded text-xs">{provider.client_id}</code>
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(provider.client_id, `client-${provider.id}`)}>
                                    {copiedText === `client-${provider.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                  </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground w-24">Secret:</span>
                                  <code className="bg-muted px-2 py-1 rounded text-xs">
                                    {showSecrets[provider.id] ? provider.client_secret_masked : '••••••••••••'}
                                  </code>
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => toggleShowSecret(provider.id)}>
                                    {showSecrets[provider.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                  </Button>
                                </div>
                              </div>

                              {/* URLs */}
                              <div className="mt-4 p-3 rounded-lg bg-muted/50 space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <Link2 className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">Callback URL:</span>
                                  <code className="text-xs flex-1 truncate">{provider.callback_url}</code>
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(provider.callback_url, `callback-${provider.id}`)}>
                                    {copiedText === `callback-${provider.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                  </Button>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-muted-foreground">Login URL:</span>
                                  <code className="text-xs flex-1 truncate">{provider.login_url}</code>
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(provider.login_url, `login-${provider.id}`)}>
                                    {copiedText === `login-${provider.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                  </Button>
                                </div>
                              </div>

                              {/* Test Results */}
                              {testResult && (
                                <div className={`mt-3 p-3 rounded-lg ${testResult.success ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    {testResult.success ? (
                                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                                    )}
                                    <span className="text-sm font-medium">{testResult.message}</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    {Object.entries(testResult.tests).map(([key, passed]) => (
                                      <div key={key} className="flex items-center gap-1">
                                        {passed ? (
                                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                                        ) : (
                                          <XCircle className="h-3 w-3 text-red-500" />
                                        )}
                                        <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex flex-col gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleTestProvider(provider.id)}>
                              <Play className="h-4 w-4 mr-1" /> Probar
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDeleteProvider(provider.id)}>
                              <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Bubble.io Guide Tab */}
          <TabsContent value="bubble" className="space-y-4">
            {bubbleInstructions ? (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        <Boxes className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <CardTitle>Guía de Integración: Bubble.io</CardTitle>
                        <CardDescription>Configura OAuth SSO con tu aplicación de Bubble.io</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {bubbleInstructions.steps.map((step) => (
                      <div key={step.step} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold mb-2">{step.title}</h4>
                          <ul className="space-y-1">
                            {step.instructions.map((instruction, idx) => (
                              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                {instruction}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Endpoints de Bubble.io</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(bubbleInstructions.endpoints).map(([key, url]) => (
                        <div key={key} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                          <Badge variant="outline" className="capitalize">{key}</Badge>
                          <code className="text-xs flex-1">{url}</code>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ejemplo de Configuración</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(bubbleInstructions.example_config, null, 2)}
                    </pre>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Solución de Problemas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {bubbleInstructions.troubleshooting.map((item, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="h-4 w-4 text-yellow-500" />
                            <span className="font-medium text-sm">{item.issue}</span>
                          </div>
                          <p className="text-sm text-muted-foreground pl-6">{item.solution}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                <p>Cargando guía...</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Provider Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Nuevo Proveedor OAuth
            </DialogTitle>
            <DialogDescription>
              Configura un nuevo proveedor OAuth para Single Sign-On
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Proveedor</Label>
              <Select 
                value={formData.provider_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, provider_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bubble">
                    <div className="flex items-center gap-2">
                      <Boxes className="h-4 w-4 text-blue-500" />
                      Bubble.io
                    </div>
                  </SelectItem>
                  <SelectItem value="google">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-red-500" />
                      Google
                    </div>
                  </SelectItem>
                  <SelectItem value="microsoft">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-600" />
                      Microsoft Azure AD
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Custom OAuth 2.0
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nombre del Proveedor *</Label>
              <Input
                placeholder="Ej: Mi App Bubble"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client ID *</Label>
                <Input
                  placeholder="App ID de tu proveedor"
                  value={formData.client_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_id: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Client Secret *</Label>
                <Input
                  type="password"
                  placeholder="App Secret de tu proveedor"
                  value={formData.client_secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                />
              </div>
            </div>

            {/* Bubble.io specific fields */}
            {formData.provider_type === 'bubble' && (
              <div className="space-y-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-blue-500" />
                  Configuración Bubble.io
                </h4>
                <div className="space-y-2">
                  <Label>Nombre de la App de Bubble *</Label>
                  <Input
                    placeholder="nombre-de-tu-app (sin .bubbleapps.io)"
                    value={formData.config.bubble_app_name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, bubble_app_name: e.target.value }
                    }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Solo el nombre, por ejemplo: si tu app es "mi-app.bubbleapps.io", escribe "mi-app"
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Crear usuarios automáticamente</Label>
                    <p className="text-xs text-muted-foreground">
                      Si el usuario no existe en OkReserva, se creará automáticamente
                    </p>
                  </div>
                  <Switch
                    checked={formData.config.auto_create_users}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, auto_create_users: checked }
                    }))}
                  />
                </div>
              </div>
            )}

            {/* Microsoft specific fields */}
            {formData.provider_type === 'microsoft' && (
              <div className="space-y-2">
                <Label>Tenant ID</Label>
                <Input
                  placeholder="Ej: common o tu-tenant-id"
                  value={formData.config.tenant_id || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, tenant_id: e.target.value }
                  }))}
                />
              </div>
            )}

            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                <strong>Importante:</strong> Asegúrate de agregar esta URL de callback en tu proveedor:
              </p>
              <code className="text-xs block mt-2 bg-background p-2 rounded">
                https://okreserva.okgo.mx/api/oauth/callback/{formData.provider_type}
              </code>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreateProvider}>
              Crear Proveedor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instructions Dialog */}
      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-blue-500" />
              Guía de Integración: Bubble.io
            </DialogTitle>
          </DialogHeader>

          {bubbleInstructions && (
            <div className="space-y-6 py-4">
              {bubbleInstructions.steps.map((step) => (
                <div key={step.step} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">{step.title}</h4>
                    <ul className="space-y-1">
                      {step.instructions.map((instruction, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                          {instruction}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowInstructions(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
